import React from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { HiXMark, HiMagnifyingGlass, HiArrowLeft, HiArrowRight } from 'react-icons/hi2';
import { HiKey } from 'react-icons/hi';

/**
 * Keyboard Shortcuts Help Modal
 * Displays all available keyboard shortcuts
 */
const KeyboardShortcutsHelp = ({ isOpen, onClose }) => {
  const shortcuts = [
    {
      keys: ['/'],
      description: 'Focus search bar',
      icon: <HiMagnifyingGlass className="text-xl" />,
    },
    {
      keys: ['Esc'],
      description: 'Close modals and dropdowns',
      icon: <HiXMark className="text-xl" />,
    },
    {
      keys: ['?'],
      description: 'Show keyboard shortcuts',
      icon: <HiKey className="text-xl" />,
    },
    {
      keys: ['Alt', '←'],
      description: 'Navigate back',
      icon: <HiArrowLeft className="text-xl" />,
    },
    {
      keys: ['Alt', '→'],
      description: 'Navigate forward',
      icon: <HiArrowRight className="text-xl" />,
    },
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-800 p-8 shadow-2xl border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
                <HiKey className="text-white text-2xl" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white">
                  Keyboard Shortcuts
                </DialogTitle>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Speed up your navigation
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <HiXMark className="text-2xl" />
            </button>
          </div>

          {/* Shortcuts List */}
          <div className="space-y-3">
            {shortcuts.map((shortcut, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="text-orange-500 dark:text-orange-400">
                  {shortcut.icon}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {shortcut.description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {shortcut.keys.map((key, keyIndex) => (
                    <React.Fragment key={keyIndex}>
                      {keyIndex > 0 && (
                        <span className="text-gray-400 dark:text-gray-500 mx-1">+</span>
                      )}
                      <kbd className="px-3 py-1.5 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-sm">
                        {key}
                      </kbd>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Press <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-semibold">Esc</kbd> to close
            </p>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default KeyboardShortcutsHelp;

